
import { supabase } from '../client';
import { SubscriptionType } from '../types';
import { addDays } from 'date-fns';

// Subscription-related functions
export const createSubscription = async (userId: string, totalClasses: number = 8): Promise<{ success: boolean; error?: string; id?: string }> => {
  try {
    console.log('Creating subscription for user:', userId);
    
    // Verify we have a valid user ID
    if (!userId) {
      console.error('Cannot create subscription: No user ID provided');
      return { success: false, error: 'User ID is required to create a subscription' };
    }
    
    const purchaseDate = new Date();
    const expirationDate = addDays(purchaseDate, 40); // Changed from 2 months to 40 days
    
    // First get the authenticated user to ensure we're properly authenticated
    const { data: authData } = await supabase.auth.getSession();
    const authUserId = authData.session?.user?.id;
    
    console.log('Current authenticated user:', authUserId);
    console.log('Creating subscription for user ID:', userId);
    
    // Use the RPC endpoint if creating subscription for another user (admin function)
    if (authUserId && authUserId !== userId) {
      console.log('Admin creating subscription for another user');
      // This would require an admin RPC function that we'd need to create
    }
    
    // Create subscription record
    const { data, error } = await supabase
      .from('subscriptions')
      .insert({
        user_id: userId,
        purchase_date: purchaseDate.toISOString(),
        expiration_date: expirationDate.toISOString(),
        total_classes: totalClasses,
        remaining_classes: totalClasses,
        active: false, // Changed to false until payment is confirmed
        payment_status: 'pending' // Set initial payment status
      })
      .select('id')
      .single();
    
    if (error) {
      console.error('Error creating subscription:', error);
      throw new Error(`Failed to create subscription: ${error.message}`);
    }
    
    return { success: true, id: data?.id };
  } catch (error: any) {
    console.error('Subscription creation error:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while creating subscription' };
  }
};

export const fetchUserSubscriptions = async (userId: string): Promise<SubscriptionType[]> => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .order('purchase_date', { ascending: false });
    
    if (error) {
      console.error('Error fetching subscriptions:', error);
      throw new Error(`Failed to fetch subscriptions: ${error.message}`);
    }
    
    return (data || []).map(item => ({
      id: item.id,
      userId: item.user_id,
      purchaseDate: item.purchase_date,
      expirationDate: item.expiration_date,
      totalClasses: item.total_classes,
      remainingClasses: item.remaining_classes,
      active: item.active,
      payment_status: item.payment_status || 'pending'
    }));
  } catch (error: any) {
    console.error('Error processing subscriptions:', error);
    throw new Error(`Failed to process subscriptions: ${error.message}`);
  }
};

// Function to fetch all subscriptions (for admin)
export const fetchAllSubscriptions = async (): Promise<SubscriptionType[]> => {
  try {
    // First fetch all subscriptions
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('subscriptions')
      .select('*')
      .order('purchase_date', { ascending: false });
    
    if (subscriptionError) {
      console.error('Error fetching subscriptions:', subscriptionError);
      throw new Error(`Failed to fetch subscriptions: ${subscriptionError.message}`);
    }
    
    // Process subscription data and get user ids
    const userIds = subscriptionData.map(sub => sub.user_id);
    
    // Then fetch profiles for those users
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', userIds);
    
    if (profileError) {
      console.error('Error fetching profiles:', profileError);
      console.warn('Will return subscription data without user information');
      
      // Return subscriptions without user info if profile fetch fails
      return (subscriptionData || []).map(item => ({
        id: item.id,
        userId: item.user_id,
        purchaseDate: item.purchase_date,
        expirationDate: item.expiration_date,
        totalClasses: item.total_classes,
        remainingClasses: item.remaining_classes,
        active: item.active,
        userName: 'Unknown User',
        userEmail: '',
        payment_status: item.payment_status || 'pending'
      }));
    }
    
    // Create a map of user profiles by id for easy lookup
    const profileMap = (profileData || []).reduce((map, profile) => {
      map[profile.id] = profile;
      return map;
    }, {} as Record<string, any>);
    
    // Map subscriptions with user data
    return (subscriptionData || []).map(item => {
      const userProfile = profileMap[item.user_id];
      const userName = userProfile 
        ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() 
        : 'Unknown User';
      
      return {
        id: item.id,
        userId: item.user_id,
        purchaseDate: item.purchase_date,
        expirationDate: item.expiration_date,
        totalClasses: item.total_classes,
        remainingClasses: item.remaining_classes,
        active: item.active,
        userName: userName || 'Unknown User',
        userEmail: '', // We don't have direct access to email from profiles table
        payment_status: item.payment_status || 'pending'
      };
    });
  } catch (error: any) {
    console.error('Error processing all subscriptions:', error);
    throw new Error(`Failed to process all subscriptions: ${error.message}`);
  }
};

// Update subscription status
export const updateSubscriptionStatus = async (subscriptionId: string, active: boolean): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('subscriptions')
      .update({ active })
      .eq('id', subscriptionId);
    
    if (error) {
      console.error('Error updating subscription status:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Subscription status update error:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while updating subscription' };
  }
};

// Update subscription payment status
export const updateSubscriptionPaymentStatus = async (
  subscriptionId: string, 
  payment_status: 'pending' | 'paid' | 'nptr'
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('subscriptions')
      .update({ payment_status })
      .eq('id', subscriptionId);
    
    if (error) {
      console.error('Error updating subscription payment status:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Subscription payment status update error:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while updating subscription payment status' };
  }
};

// Edit subscription (update classes and expiration date)
export const editSubscription = async (
  subscriptionId: string, 
  updates: { 
    totalClasses?: number; 
    remainingClasses?: number;
    expirationDate?: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('subscriptions')
      .update({
        total_classes: updates.totalClasses,
        remaining_classes: updates.remainingClasses,
        expiration_date: updates.expirationDate
      })
      .eq('id', subscriptionId);
    
    if (error) {
      console.error('Error updating subscription:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Subscription update error:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while updating subscription' };
  }
};

// Delete subscription
export const deleteSubscription = async (subscriptionId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('subscriptions')
      .delete()
      .eq('id', subscriptionId);
    
    if (error) {
      console.error('Error deleting subscription:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Subscription deletion error:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while deleting subscription' };
  }
};
